import { ActionButtonProps } from '@pages/ActionButton';
import { billingStore, Product, Subscription } from '@stores/Billing';
import { userStore } from '@stores/User';

/**
 * Determines CTA props for a product based on user state and current subscription
 * @param product - The product to generate CTA props for
 * @param currentSubscription - The user's current subscription (undefined while billing loads)
 * @param onUpgrade - Callback for upgrade actions
 * @param onDowngrade - Callback for downgrade actions
 * @param navigate - Navigation function (from useNavigate hook)
 * @returns CTA props object
 */
export const determineCtaProps = (
  product: Product,
  currentSubscription: Subscription | undefined,
  onUpgrade: (product: Product) => void | Promise<void>,
  onDowngrade: (product: Product) => void | Promise<void>,
  navigate: (path: string) => void,
) => {
  // Already on this exact plan.
  if (currentSubscription?.product?.id && product.id === currentSubscription.product.id) {
    return {
      text: 'Current plan',
      variant: 'secondary-cta' as ActionButtonProps['variant'],
      onClick: () => {
        return;
      },
    };
  }

  // No paid plan yet — logged out OR logged in on Free. One login-neutral label;
  // logged-out users sign up first, logged-in users go straight to checkout.
  const hasPaidSubscription = Boolean(currentSubscription?.product?.id);
  if (!hasPaidSubscription) {
    return {
      text: `Get ${product.name}`,
      variant: 'primary-cta' as ActionButtonProps['variant'],
      onClick: userStore.isLoggedIn
        ? async () => await onUpgrade(product)
        : () => navigate('/login'),
    };
  }

  // Has a paid plan: this is an up- or down-grade from it.
  const isDowngrade =
    product.price.unit_amount < (currentSubscription?.product?.price.unit_amount ?? 0);

  return {
    text: `${isDowngrade ? 'Downgrade' : 'Upgrade'} to ${product.name}`,
    variant: (isDowngrade ? 'secondary' : 'primary-cta') as ActionButtonProps['variant'],
    onClick: isDowngrade
      ? async () => await onDowngrade(product)
      : async () => await onUpgrade(product),
  };
};

/**
 * Fetches available subscriptions from the billing store
 * @returns Promise that resolves to array of products
 */
export const fetchAvailableSubscriptions = async (): Promise<Product[]> => {
  try {
    const result = await billingStore.getAvailableSubscriptions();
    return result as Product[];
  } catch (error) {
    console.error('Error fetching available subscriptions:', error);
    return [];
  }
};

/**
 * Fetches available one-time products from the billing store
 * @returns Promise that resolves to array of products
 */
export const fetchAvailableOneTimeProducts = async (): Promise<Product[]> => {
  try {
    const result = await billingStore.getAvailableOneTimeProducts();
    return result as Product[];
  } catch (error) {
    console.error('Error fetching available one-time products:', error);
    return [];
  }
};

/**
 * Fetches all pricing data for the pricing page
 * @returns Promise that resolves to object with subscriptions and one-time products
 */
export const fetchPricingData = async () => {
  const [subscriptions, oneTimeProducts] = await Promise.all([
    fetchAvailableSubscriptions(),
    fetchAvailableOneTimeProducts(),
  ]);
  return { subscriptions, oneTimeProducts };
};
